import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { ModalController } from "@ionic/angular";
import { TranslateService } from "@ngx-translate/core";
import { Subscription } from "rxjs";
import { DocumentSecurityType } from "src/model/class/general classes/document-security";
import { AlertService } from "src/service/alert.service";
import { DocumentSecurityService } from "src/service/document-security.service";
import { LoaderService } from "src/service/loader.service";

/**
 * Super-admin-only modal to manage the global document type catalog: list the
 * active types, add a new one and logically delete an existing one. Visibility
 * of the entry button is gated on the frontend, while creation/deletion are
 * also enforced server-side (super admin).
 */
@Component({
    selector: "app-popup-document-security-types",
    templateUrl: "./popup-document-security-types.component.html",
    styleUrls: ["./popup-document-security-types.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PopupDocumentSecurityTypesComponent implements OnInit, OnDestroy {
  public types: Array<DocumentSecurityType> = [];
  public newTypeName: string = "";
  public isLoading: boolean = false;
  public changed: boolean = false;

  public titleLabel: string = "";
  public namePlaceholderLabel: string = "";
  public addLabel: string = "";
  public emptyLabel: string = "";
  public deleteConfirmLabel: string = "";

  private _translationSubscription: Subscription = null;

  constructor(
    private _modalController: ModalController,
    private _documentSecurityService: DocumentSecurityService,
    private _alertService: AlertService,
    private _loaderService: LoaderService,
    private _translateService: TranslateService,
  ) {}

  /**
   * Load translations and the current type catalog when the modal opens.
   */
  ngOnInit(): void {
    this.loadTranslation();
    this.subscribeTranslationEvent();
    this.loadTypes();
  }

  /**
   * Unsubscribe from the language-change stream to avoid leaks.
   */
  ngOnDestroy(): void {
    this._translationSubscription?.unsubscribe();
  }

  /**
   * Close the modal, reporting whether the catalog was modified so the caller
   * can refresh its type-dependent state.
   */
  public onClose(): void {
    this._modalController.dismiss({ changed: this.changed });
  }

  /**
   * Create a new type from the trimmed input, then clear the field, flag the
   * catalog as changed and reload the list. No-op on empty input.
   */
  public async onAddType(): Promise<void> {
    const name = (this.newTypeName || "").trim();
    if (!name) return;

    try {
      this.isLoading = true;
      await this._loaderService.presentLoaderWaiting();
      await this._documentSecurityService.createDocumentType(name);
      this.newTypeName = "";
      this.changed = true;
      await this.loadTypes();
    } catch {
      await this._alertService.presentAlertSomethingWrong();
    } finally {
      await this._loaderService.dismissLoaderWaiting();
      this.isLoading = false;
    }
  }

  /**
   * Logically delete a type after user confirmation, flag the catalog as
   * changed and reload the list.
   */
  public async onDeleteType(type: DocumentSecurityType): Promise<void> {
    const confirmed = await this._alertService.presentAlertConfirmOrDismiss(
      this.titleLabel,
      "",
      this.deleteConfirmLabel,
    );
    if (!confirmed) return;

    try {
      this.isLoading = true;
      await this._loaderService.presentLoaderWaiting();
      await this._documentSecurityService.deleteDocumentType(type.id);
      this.changed = true;
      await this.loadTypes();
    } catch {
      await this._alertService.presentAlertSomethingWrong();
    } finally {
      await this._loaderService.dismissLoaderWaiting();
      this.isLoading = false;
    }
  }

  /**
   * Fetch the active type catalog, falling back to an empty list on failure.
   */
  private async loadTypes(): Promise<void> {
    try {
      this.types = await this._documentSecurityService.getDocumentTypes();
    } catch {
      this.types = [];
    }
  }

  /**
   * Resolve all static labels for the active language; re-run on language change.
   */
  private loadTranslation(): void {
    this._translateService
      .get([
        "dictionary.documentTypesManage",
        "dictionary.documentTypeName",
        "dictionary.documentTypeAdd",
        "dictionary.documentTypesEmpty",
        "dictionary.documentTypeDeleteConfirm",
      ])
      .subscribe((translation) => {
        this.titleLabel = translation["dictionary.documentTypesManage"];
        this.namePlaceholderLabel = translation["dictionary.documentTypeName"];
        this.addLabel = translation["dictionary.documentTypeAdd"];
        this.emptyLabel = translation["dictionary.documentTypesEmpty"];
        this.deleteConfirmLabel =
          translation["dictionary.documentTypeDeleteConfirm"];
      });
  }

  /**
   * Re-resolve the labels whenever the app language changes at runtime.
   */
  private subscribeTranslationEvent(): void {
    this._translationSubscription =
      this._translateService.onLangChange.subscribe(() => {
        this.loadTranslation();
      });
  }
}
